import React, { useState, useEffect } from "react";
import "../../../styles/GrammarCategories.css";
import Part5_Grammar from "./Part5_Grammar";
import VocabFlashcards from "./VocabFlashcards";
import GrammarSummary from "./GrammarSummary";
import Modal from "react-modal";
import { db } from "../../../firebase";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { useAuth } from "../../../contexts/AuthContext";

const GrammarCategories = ({ onBack }) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [showGrammarSummary, setShowGrammarSummary] = useState(false);
  const [isStudyModeModalOpen, setIsStudyModeModalOpen] = useState(false);
  const [tempCategoryId, setTempCategoryId] = useState(null);
  const [showNoWrongQuestionsModal, setShowNoWrongQuestionsModal] =
    useState(false);
  const [studyMode, setStudyMode] = useState("all"); // "all" 또는 "wrong"
  const [completedCategories, setCompletedCategories] = useState({});
  const { currentUser } = useAuth();

  const categories = [
    { id: 15, title: "비즈니스 이메일 1" },
    { id: 101, title: "비즈니스 이메일 2" },
    { id: 102, title: "비즈니스 이메일 3" },
    { id: 16, title: "기사문 1" },
    { id: 104, title: "기사문 2" },
    { id: 10, title: "공지글/광고 1" },
    { id: 105, title: "공지글/광고 2" },
    { id: 106, title: "공지글/광고 3" },
    { id: 2, title: "태 (능동태/수동태) 문제" },
    { id: 3, title: "주어-동사 수일치 문제" },
    { id: 6, title: "비교급/최상급 문제" },
    { id: 7, title: "전치사" },
    { id: 8, title: "접속사/접속부사" },
    { id: 9, title: "대명사 (인칭대명사, 소유격, 지시대명사 구분)" },
    { id: 11, title: "동명사/부정사 선택 (to부정사 vs 동명사)" },
  ];

  useEffect(() => {
    if (!currentUser) return;

    const userDocRef = doc(db, "users", currentUser.uid);

    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();

          // part5.grammar 데이터 구조 확인
          const grammarData = data?.part5?.grammar || {};
          const newCompletedCategories = {};

          // 각 카테고리의 완료 상태 확인
          Object.keys(grammarData).forEach((categoryId) => {
            if (grammarData[categoryId]?.completed) {
              newCompletedCategories[categoryId] = true;
            }
          });

          setCompletedCategories(newCompletedCategories);
        }
      },
      (error) => {
        console.error("Firestore 리스너 에러:", error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [currentUser]);

  const handleCategoryClick = (id) => {
    setTempCategoryId(id);
    setIsStudyModeModalOpen(true);
  };

  const checkWrongQuestions = async (categoryId) => {
    if (!currentUser) return false;

    try {
      const userDocRef = doc(db, "users", currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) return false;

      const data = userDoc.data();
      const wrongQuestions =
        data?.part5?.grammar?.[categoryId]?.wrongQuestions || [];

      return wrongQuestions.length > 0;
    } catch (error) {
      console.error("틀린 문제 확인 중 오류:", error);
      return false;
    }
  };

  const handleStudyModeSelect = async (mode) => {
    if (mode === "wrong") {
      const hasWrongQuestions = await checkWrongQuestions(tempCategoryId);

      if (!hasWrongQuestions) {
        setShowNoWrongQuestionsModal(true);
        setIsStudyModeModalOpen(false);
        return;
      }
    }

    setStudyMode(mode);
    setSelectedCategoryId(tempCategoryId);
    setIsStudyModeModalOpen(false);
  };

  if (selectedCategoryId === 12) {
    return <VocabFlashcards onBack={() => setSelectedCategoryId(null)} />;
  }

  if (selectedCategoryId) {
    return (
      <Part5_Grammar
        categoryId={selectedCategoryId}
        studyMode={studyMode}
        onBack={() => {
          setSelectedCategoryId(null);
          setStudyMode("all");
        }}
      />
    );
  }

  return (
    <div className="grammar-categories">
      <button className="back-button" onClick={onBack}>
        ← 뒤로가기
      </button>
      <h2>TOEIC Part 5 문법 문제 유형</h2>
      <div className="category-list">
        {categories.map((category, index) => (
          <React.Fragment key={category.id}>
            <div
              className="part5-category-item"
              onClick={() => handleCategoryClick(category.id)}
            >
              <h3>{category.title}</h3>
              {completedCategories[category.id] && (
                <span className="completion-status">학습 완료!</span>
              )}
            </div>
            {category.id === 106 && <div className="category-divider"></div>}
          </React.Fragment>
        ))}
      </div>

      <Modal
        isOpen={isStudyModeModalOpen}
        onRequestClose={() => setIsStudyModeModalOpen(false)}
        className="study-mode-modal-content"
        overlayClassName="study-mode-modal-overlay"
      >
        <div className="study-mode-modal-header">
          <h3>학습 모드 선택</h3>
          <button
            className="modal-close-btn"
            onClick={() => setIsStudyModeModalOpen(false)}
          >
            ✕
          </button>
        </div>
        <div className="study-mode-options">
          <button
            className="study-mode-button all"
            onClick={() => handleStudyModeSelect("all")}
          >
            전체 문제 풀기
          </button>
          <button
            className="study-mode-button wrong"
            onClick={() => handleStudyModeSelect("wrong")}
            disabled={!currentUser}
          >
            틀린 문제만 풀기
            {!currentUser && (
              <span className="login-required">(로그인 필요)</span>
            )}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showNoWrongQuestionsModal}
        onRequestClose={() => setShowNoWrongQuestionsModal(false)}
        className="no-wrong-questions-modal"
        overlayClassName="no-wrong-questions-overlay"
      >
        <div className="text-center p-8">
          <h2 className="text-2xl font-bold mb-4">📚 알림</h2>
          <p className="text-gray-600 mb-6">
            현재 틀린 문제가 없습니다!
            <br />
            전체 문제 풀이를 진행해주세요.
          </p>
          <button
            onClick={() => setShowNoWrongQuestionsModal(false)}
            className="modal-button"
          >
            확인
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default GrammarCategories;
